import React, { type RefObject, useCallback } from 'react';
import { Platform, StyleSheet, View, type ViewProps } from 'react-native';

import { SM, XS, theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';
import {
  FlashList,
  type FlashListProps,
  type FlashListRef,
  type ListRenderItemInfo,
} from '@shopify/flash-list';

import { ModifierGroupVariation } from '@sg/graphql-schema';
import {
  CustomizationIngredientsGrid,
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  CustomizationModificationItem,
  useCustomizationContext,
  useCustomizationHelpers,
  useCustomizationStepperTimeout,
} from '@sg/shared-fe-features-customization';
import { useDietaryRestrictions } from '@sg/shared-fe-features-menu';

import { useDietaryPreferencesMachine } from '../../../../../../../../context';
import { type CustomizationIngredientRow } from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Virtualized ingredients grid. Sections are flattened into rows (heading, then
 * one row per ingredient) and rendered by `FlashList`, so only rows near the
 * viewport are mounted. Ingredients flow through a column grid one card per
 * cell while headings span the full width.
 */
export const CustomizationIngredientsList = (props: Props) => {
  const {
    ref,
    rows,
    itemsPerRow,
    actor,
    initialScrollIndex,
    contentContainerStyle,
    getSectionHelperText,
    getSectionSubheading,
    onViewableItemsChanged,
  } = props;

  const { match } = useResponsive();

  const { activeStepperId, setActiveStepperId, resetStepperTimeout, clearStepperTimeout } =
    useCustomizationStepperTimeout({ resetOnWindowBlur: true });

  const { restrictions } = useDietaryPreferencesMachine({ referrer: 'Customization' });
  const getRestrictionNotice = useDietaryRestrictions({ restrictions });

  const {
    addIngredient,
    removeIngredient,
    toggleSplitPortion,
    checkIfCanAddMoreModifications,
    getMaxPortionsForGroup,
    getUpsellPriceForIngredient,
    getPortionOptionsForIngredient,
  } = useCustomizationHelpers({ actor });

  const {
    allModificationsMap,
    modifications,
    activeModificationsMap,
    hasReachedMaxModifiersCounts,
    shouldUseUpsellPricing,
  } = useCustomizationContext({ actor });

  // ─── Derived ─────────────────────────────────────────────────────────

  const drawDistance = match([DRAW_DISTANCE_XS, DRAW_DISTANCE_SM]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const rowStyles = match([styles.rowXS, styles.rowSM]);

  const ingredientCellSpacedStyles = match([
    styles.ingredientCellSpacedXS,
    styles.ingredientCellSpacedSM,
  ]);

  const contentContainerStyles = [
    styles.contentContainer,
    match([styles.contentContainerXS, styles.contentContainerSM]),
    contentContainerStyle,
  ];

  // ─── Handlers ────────────────────────────────────────────────────────

  const renderIngredient = useCallback(
    (row: IngredientRow) => {
      const { group, modification } = row;

      const ingredientId = modification.ingredient?.id;
      const modificationEntry = ingredientId ? allModificationsMap?.get(ingredientId) : null;

      if (!modificationEntry) {
        return null;
      }

      const { modificationGroupName } = modificationEntry;

      const allergenProperties = modification.ingredient?.allergenProperties ?? [];
      const restrictionNotice = getRestrictionNotice({ allergenProperties });

      const canAddMoreWithinGroup = checkIfCanAddMoreModifications(modificationGroupName);
      const canAddMore = canAddMoreWithinGroup && !hasReachedMaxModifiersCounts;
      const maxPortionsForGroup = getMaxPortionsForGroup(modificationGroupName);
      const isSplitPortion = group.variation === ModifierGroupVariation.SplitPortion;
      const upsellPrice = getUpsellPriceForIngredient(ingredientId);
      const quantity = activeModificationsMap.get(modification.id)?.quantity ?? 0;

      const isSelected = activeModificationsMap.has(modification.id);
      const isStepperActive = activeStepperId === ingredientId;

      const portionOptions = getPortionOptionsForIngredient(ingredientId);
      const hasPortionOptions = Boolean(portionOptions?.length);

      return (
        <CustomizationModificationItem
          key={modification.id}
          quantity={quantity}
          isSelected={isSelected}
          isStepperActive={isStepperActive}
          canAddMore={canAddMore}
          maxPortionsForGroup={maxPortionsForGroup}
          modification={modification}
          notice={restrictionNotice}
          upsellPrice={upsellPrice}
          portionOptions={isStepperActive ? portionOptions : undefined}
          hasPortionOptions={hasPortionOptions}
          isUpsellPricingEnabled={shouldUseUpsellPricing}
          onIncrement={addIngredient}
          onDecrement={removeIngredient}
          onToggleSplitPortion={toggleSplitPortion}
          isSplitPortion={isSplitPortion}
          resetStepperTimeout={resetStepperTimeout}
          clearStepperTimeout={clearStepperTimeout}
          setActiveStepperId={setActiveStepperId}
          dataSet={row.dataSet}
        />
      );
    },
    [
      activeModificationsMap,
      activeStepperId,
      addIngredient,
      allModificationsMap,
      checkIfCanAddMoreModifications,
      clearStepperTimeout,
      getMaxPortionsForGroup,
      getPortionOptionsForIngredient,
      getRestrictionNotice,
      getUpsellPriceForIngredient,
      hasReachedMaxModifiersCounts,
      shouldUseUpsellPricing,
      removeIngredient,
      resetStepperTimeout,
      setActiveStepperId,
      toggleSplitPortion,
    ],
  );

  const renderRow = useCallback(
    (row: CustomizationIngredientRow) => {
      const { group } = row;

      if (row.kind === 'section-heading') {
        return (
          <CustomizationIngredientsGrid.SectionHeading
            ingredientKindName={group.displayName}
            subheading={getSectionSubheading?.(group)}
            helperText={getSectionHelperText?.(group)}
            style={styles.sectionHeading}
            dataSet={row.dataSet}
          />
        );
      }

      // NOTE: The closing line of a section drops its bottom spacing, leaving
      //       the next heading's own top spacing to separate the two sections.
      const cellStyles = row.isInLastRow ? styles.ingredientCell : ingredientCellSpacedStyles;

      // NOTE: The grid positions its own cells, so each one carries the list
      //       item role itself to keep the ingredients readable as a list.
      return (
        <View style={cellStyles} role="listitem">
          {renderIngredient(row)}
        </View>
      );
    },
    [getSectionHelperText, getSectionSubheading, ingredientCellSpacedStyles, renderIngredient],
  );

  const renderItem = useCallback(
    (info: ListRenderItemInfo<CustomizationIngredientRow>) => (
      <View style={rowStyles}>{renderRow(info.item)}</View>
    ),
    [renderRow, rowStyles],
  );

  // ─────────────────────────────────────────────────────────────────────

  if (!allModificationsMap || !modifications) {
    return null;
  }

  return (
    <FlashList
      ref={ref}
      data={rows}
      keyExtractor={extractRowKey}
      getItemType={getRowType}
      renderItem={renderItem}
      numColumns={itemsPerRow}
      overrideItemLayout={overrideRowLayout}
      drawDistance={drawDistance}
      initialScrollIndex={initialScrollIndex}
      viewabilityConfig={VIEWABILITY_CONFIG}
      onViewableItemsChanged={onViewableItemsChanged}
      role="list"
      style={styles.list}
      contentContainerStyle={contentContainerStyles}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Only ingredient rows occupy a single column; headings span the grid.
 */
function overrideRowLayout(
  layout: { span?: number },
  row: CustomizationIngredientRow,
  _index: number,
  maxColumns: number,
) {
  if (row.kind === 'ingredient') {
    return;
  }

  layout.span = maxColumns;
}

function extractRowKey(row: CustomizationIngredientRow) {
  return row.key;
}

function getRowType(row: CustomizationIngredientRow) {
  return row.kind;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// NOTE: Web renders large amounts of content far more cheaply than native, so
//       it can afford a much larger draw window. That keeps more of the grid in
//       the DOM for find-in-page, assistive tech and scroll-to-section jumps.

const DRAW_DISTANCE_XS = Platform.select({ web: 5000, default: 1000 });
const DRAW_DISTANCE_SM = Platform.select({ web: 5000, default: 1600 });

const CONTENT_MAX_WIDTH = 1040;

const COLUMN_GAP_XS = theme.spacing['2'];
const COLUMN_GAP_SM = theme.spacing['4'];
const CELL_GUTTER_XS = COLUMN_GAP_XS / 2;
const CELL_GUTTER_SM = COLUMN_GAP_SM / 2;
const CONTENT_INSET_XS = XS.MARGIN - CELL_GUTTER_XS;
const CONTENT_INSET_SM = SM.MARGIN - CELL_GUTTER_SM;

const VIEWABILITY_CONFIG: FlashListProps<CustomizationIngredientRow>['viewabilityConfig'] = {
  itemVisiblePercentThreshold: 50,
  minimumViewTime: 0,
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  list: {
    flex: 1,
    backgroundColor: theme.colors.OATMEAL,
  },
  contentContainer: {
    maxWidth: CONTENT_MAX_WIDTH,
  },
  contentContainerXS: {
    paddingHorizontal: CONTENT_INSET_XS,
  },
  contentContainerSM: {
    paddingHorizontal: CONTENT_INSET_SM,
  },
  rowXS: {
    flex: 1,
    paddingHorizontal: CELL_GUTTER_XS,
  },
  rowSM: {
    flex: 1,
    paddingHorizontal: CELL_GUTTER_SM,
  },
  ingredientCell: {
    flex: 1,
  },
  ingredientCellSpacedXS: {
    flex: 1,
    marginBottom: theme.spacing['4'],
  },
  ingredientCellSpacedSM: {
    flex: 1,
    marginBottom: theme.spacing['6'],
  },
  sectionHeading: {
    paddingBottom: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  ref?: RefObject<FlashListRef<CustomizationIngredientRow> | null>;
  rows: readonly CustomizationIngredientRow[];
  itemsPerRow: number;
  actor: CustomizationMachineActorRef;
  initialScrollIndex?: number;
  onViewableItemsChanged: ViewableItemsChangedHandler;
  getSectionHelperText?: (
    group: CustomizationMachineTypes.ExtendedModifierGroup,
  ) => string | null | undefined;
  getSectionSubheading?: (
    group: CustomizationMachineTypes.ExtendedModifierGroup,
  ) => string | undefined;
  contentContainerStyle?: ViewProps['style'];
};

type IngredientRow = Extract<CustomizationIngredientRow, { kind: 'ingredient' }>;

type ViewableItemsChangedHandler = (info: {
  viewableItems: readonly { item: CustomizationIngredientRow }[];
}) => void;
